/**
 * Reset Password Page
 * Allows users to reset password using token from email
 */

import { CircularProgress } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { RESET_PASSWORD_TEXT, RESET_PASSWORD_ERRORS } from '../../constants';
import { useAuth } from '../../context/AuthContext';
import './ForgotPage.scss';

interface ResetForm {
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  password?: string;
  confirmPassword?: string;
  token?: string;
}

export const ResetPasswordPage: React.FC = () => {
  const { resetPassword, isAuthenticated, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState<ResetForm>({ password: '', confirmPassword: '' });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setFormErrors({ token: RESET_PASSWORD_ERRORS.invalidOrMissingToken });
    }
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (error) clearError();
    setFormErrors((prev) => ({ ...prev, password: undefined, confirmPassword: undefined }));
    // Deliberately scoped to `formData` only — this clears the error as the
    // user edits the form. Adding `error`/`clearError` (as the linter
    // suggests) would also re-run this effect the instant `error` is set by
    // a failed submit, immediately clearing the message we just showed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  // Particle animation — same as login/forgot-password page
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    interface P { x: number; y: number; r: number; dx: number; dy: number; o: number; }
    const rand = (a: number, b: number) => Math.random() * (b - a) + a;
    let particles: P[] = [];
    let raf = 0;

    const resize = () => {
      const parent = canvas.parentElement!;
      const rect = parent.getBoundingClientRect();
      canvas.width = rect.width || window.innerWidth;
      canvas.height = rect.height || window.innerHeight;
      particles = Array.from({ length: 40 }, () => ({
        x: rand(0, canvas.width), y: rand(0, canvas.height),
        r: rand(1, 3.5), dx: rand(-0.3, 0.3), dy: rand(-0.25, 0.25), o: rand(0.10, 0.45),
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${p.o})`;
        ctx.fill();
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      }
      raf = requestAnimationFrame(draw);
    };

    const start = () => { resize(); draw(); };
    const timer = setTimeout(start, 50);
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); window.removeEventListener('resize', resize); };
  }, []);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!token) {
      errors.token = RESET_PASSWORD_ERRORS.invalidOrMissingToken;
      setFormErrors(errors);
      return false;
    }

    if (!formData.password) {
      errors.password = RESET_PASSWORD_ERRORS.passwordRequired;
    } else if (formData.password.length < 8) {
      errors.password = RESET_PASSWORD_ERRORS.passwordTooShort;
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = RESET_PASSWORD_ERRORS.passwordComplexity;
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = RESET_PASSWORD_ERRORS.confirmPasswordRequired;
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = RESET_PASSWORD_ERRORS.passwordsDoNotMatch;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await resetPassword({ token: token!, newPassword: formData.password });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Reset password failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fp-loading">
        <CircularProgress className="fp-spinner-loading" />
      </div>
    );
  }

  return (
    <div className="fp-root">
      <link href="https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,0,0&display=swap" rel="stylesheet" />

      {/* Left — hero image */}
      <div className="fp-left">
        <img src="/assets/login/hrms-hero.png" alt={RESET_PASSWORD_TEXT.heroImageAlt} className="fp-hero-img" />
      </div>

      {/* Right — teal + particles + card */}
      <div className="fp-right">
        <canvas ref={canvasRef} className="fp-particles-canvas" />

        <div className="fp-card">
          {formErrors.token ? (
            <div className="fp-success">
              <span className="material-symbols-outlined fp-success-icon fp-success-icon--error">
                error
              </span>
              <h2 className="fp-title">{RESET_PASSWORD_TEXT.invalidLinkTitle}</h2>
              <p className="fp-subtitle">{RESET_PASSWORD_TEXT.invalidLinkSubtitle}</p>
              <div className="fp-success-actions">
                <Link className="fp-submit" to="/auth/forgot-password">{RESET_PASSWORD_TEXT.requestNewLinkButton}</Link>
              </div>
            </div>
          ) : !isSubmitted ? (
            <>
              <div className="fp-header">
                <h2 className="fp-title">{RESET_PASSWORD_TEXT.formTitle}</h2>
                <p className="fp-subtitle">{RESET_PASSWORD_TEXT.formSubtitle}</p>
              </div>

              {error && (
                <div className="fp-alert-error" data-testid="reset-password-error-alert">
                  <span className="material-symbols-outlined">error</span>
                  <span>{error}</span>
                </div>
              )}

              <form className="fp-form" onSubmit={handleSubmit} noValidate>
                <div className={`fp-field${formErrors.password ? ' fp-field-error' : ''}`}>
                  <label className="fp-label" htmlFor="rp-password">{RESET_PASSWORD_TEXT.newPasswordLabel}</label>
                  <div className="fp-input-wrap">
                    <span className="material-symbols-outlined fp-input-icon">lock</span>
                    <input
                      id="rp-password"
                      className="fp-input fp-input-has-toggle"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange}
                      autoComplete="new-password"
                      autoFocus
                      data-testid="reset-password-new-input"
                    />
                    <button
                      type="button"
                      className="fp-input-toggle"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={RESET_PASSWORD_TEXT.togglePasswordVisibility}
                      data-testid="reset-password-toggle-new-button"
                    >
                      <span className="material-symbols-outlined">
                        {showPassword ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                  {formErrors.password && <span className="fp-field-msg" data-testid="reset-password-new-error">{formErrors.password}</span>}
                </div>

                <div className={`fp-field${formErrors.confirmPassword ? ' fp-field-error' : ''}`}>
                  <label className="fp-label" htmlFor="rp-confirm-password">{RESET_PASSWORD_TEXT.confirmPasswordLabel}</label>
                  <div className="fp-input-wrap">
                    <span className="material-symbols-outlined fp-input-icon">lock</span>
                    <input
                      id="rp-confirm-password"
                      className="fp-input fp-input-has-toggle"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      autoComplete="new-password"
                      data-testid="reset-password-confirm-input"
                    />
                    <button
                      type="button"
                      className="fp-input-toggle"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      aria-label={RESET_PASSWORD_TEXT.toggleConfirmPasswordVisibility}
                      data-testid="reset-password-toggle-confirm-button"
                    >
                      <span className="material-symbols-outlined">
                        {showConfirmPassword ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                  {formErrors.confirmPassword && (
                    <span className="fp-field-msg" data-testid="reset-password-confirm-error">{formErrors.confirmPassword}</span>
                  )}
                </div>

                <button type="submit" className="fp-submit" disabled={isSubmitting} data-testid="reset-password-submit-button">
                  {isSubmitting
                    ? <CircularProgress size={20} className="fp-spinner-submit" />
                    : RESET_PASSWORD_TEXT.submitButton}
                </button>

                <div className="fp-back-row">
                  <Link className="fp-back-link" to="/auth/login" data-testid="reset-password-back-login-link">
                    <span className="material-symbols-outlined">arrow_back</span>
                    {RESET_PASSWORD_TEXT.backToLogin}
                  </Link>
                </div>
              </form>
            </>
          ) : (
            <div className="fp-success" data-testid="reset-password-success">
              <span className="material-symbols-outlined fp-success-icon">check_circle</span>
              <h2 className="fp-title">{RESET_PASSWORD_TEXT.successTitle}</h2>
              <p className="fp-subtitle">{RESET_PASSWORD_TEXT.successSubtitle}</p>
              <div className="fp-success-actions">
                <Link
                  className="fp-submit"
                  to="/auth/login"
                  data-testid="reset-password-success-login-link"
                  state={{
                    message: RESET_PASSWORD_TEXT.loginStateMessage,
                  }}
                >
                  {RESET_PASSWORD_TEXT.signInButton}
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};